import React, { useState, useEffect, useCallback } from "react";

export const SharePresetButtons = ({ value, onChange, disabled = false }) => {
  const sliderValue = typeof value === "number" ? value : (parseFloat(value) || 0);
  
  const handlePresetClick = (presetValue) => {
    if (onChange) {
      onChange(presetValue);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => handlePresetClick(25)}
        disabled={disabled}
        className={`px-1.5 py-0.5 text-xs font-medium rounded border transition-colors ${
          sliderValue === 25 
            ? 'bg-emerald-600 text-white border-emerald-600' 
            : 'bg-white text-gray-700 border-gray-300 hover:bg-emerald-50 hover:border-emerald-400'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        25%
      </button>
      <button
        type="button"
        onClick={() => handlePresetClick(50)}
        disabled={disabled}
        className={`px-1.5 py-0.5 text-xs font-medium rounded border transition-colors ${
          sliderValue === 50 
            ? 'bg-emerald-600 text-white border-emerald-600' 
            : 'bg-white text-gray-700 border-gray-300 hover:bg-emerald-50 hover:border-emerald-400'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        50%
      </button>
      <button
        type="button"
        onClick={() => handlePresetClick(100)}
        disabled={disabled}
        className={`px-1.5 py-0.5 text-xs font-medium rounded border transition-colors ${
          sliderValue === 100 
            ? 'bg-emerald-600 text-white border-emerald-600' 
            : 'bg-white text-gray-700 border-gray-300 hover:bg-emerald-50 hover:border-emerald-400'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        100%
      </button>
    </div>
  );
};

const ShareSlider = ({ 
  value, 
  onChange, 
  onBlur,
  disabled = false,
  className = ""
}) => {
  const [localValue, setLocalValue] = useState(value ?? 0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) {
      setLocalValue(value !== undefined && value !== null && value !== "" ? parseFloat(value) : 0);
    }
  }, [value, isDragging]);

  const handleSliderChange = (e) => {
    const newValue = parseFloat(e.target.value);
    setLocalValue(newValue);
  };

  const handleSliderMouseDown = () => {
    setIsDragging(true);
  };

  const handleSliderMouseUp = useCallback((e) => {
    setIsDragging(false);
    const newValue = parseFloat(e.target.value);
    if (onChange) {
      onChange(newValue);
    }
    if (onBlur) {
      onBlur(newValue);
    }
  }, [onChange, onBlur]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      setLocalValue("");
    } else {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue)) {
        setLocalValue(numValue);
      }
    }
  };

  const handleInputBlur = () => {
    let numValue = parseFloat(localValue);
    
    if (isNaN(numValue) || localValue === "") {
      numValue = 0;
    } else if (numValue < 0) {
      numValue = 0;
    } else if (numValue > 100) {
      numValue = 100;
    }
    
    setLocalValue(numValue);
    if (onChange) {
      onChange(numValue);
    }
    if (onBlur) {
      onBlur(numValue);
    }
  };

  const sliderValue = typeof localValue === "number" ? localValue : (parseFloat(localValue) || 0);
  const clampedSliderValue = Math.min(100, Math.max(0, sliderValue));

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <input
        type="range"
        min="0"
        max="100"
        step="1"
        value={clampedSliderValue}
        onChange={handleSliderChange}
        onMouseDown={handleSliderMouseDown}
        onMouseUp={handleSliderMouseUp}
        onTouchStart={handleSliderMouseDown}
        onTouchEnd={handleSliderMouseUp}
        disabled={disabled}
        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: disabled 
            ? '#e5e7eb' 
            : `linear-gradient(to right, #059669 0%, #059669 ${clampedSliderValue}%, #e5e7eb ${clampedSliderValue}%, #e5e7eb 100%)`
        }}
      />
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="0"
          max="100"
          value={localValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          className="w-14 px-2 py-1 border border-gray-300 rounded-md text-sm text-center focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:bg-gray-100 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <span className="text-sm text-gray-600">%</span>
      </div>
    </div>
  );
};

export default ShareSlider;
